/**
 * The caller's plan id, for the surfaces that PACKAGE a feature rather than
 * bill for it.
 *
 * Shares `useCurrentSubscription`'s query key on purpose, so the billing page
 * and the transport ceiling are one cached read rather than two calls for the
 * same row.
 *
 * Every failure mode answers `null`, and `null` means "unrestricted" to every
 * consumer (see `transportCapabilitiesForPlan`). That direction is deliberate:
 * a subscriptions API that is slow or unreachable must not silently strip
 * capabilities from a paying tenant — a packaging decision the client cannot
 * verify is one it should not enforce. The backend gates the entitlements that
 * actually matter.
 *
 * Three answers are NOT failures, and resolve to the free tier: a read that
 * succeeded with no row (the customer subscribes to nothing), a row whose
 * status is cancelled/expired, and a row with no status at all. There the plan
 * IS known, and free is the one the customer is on — the same answers
 * `commons.authz.entitlements.granted_for_subscription` gives the authorizer.
 */
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import * as React from "react";
import { PLAN_FREE, bundledPlans, resolvePlanById, type Plan } from "@authe/fe-commons/plans";
import {
  createApiSubscriptionsSource,
  isEntitledStatus,
  type SubscriptionSummary,
} from "@/api/subscriptionsSource";

/** The shared cache key for "the caller's current subscription". */
export const CURRENT_SUBSCRIPTION_QUERY_KEY = ["subscriptions", "current"] as const;

/**
 * Plan id a subscription grants right now, or `null` when unknown.
 *
 * - `undefined` — the read has not landed, or failed: unknown.
 * - `null` — the read succeeded and there is no row: the free tier.
 * - a row that is not entitled (terminal, or no status): the free tier.
 * - an entitled row: its own plan id, or `null` when it names none
 *   (`plan_by_id(None)` in the Python — no such tier, so unknown).
 */
export function planIdFor(subscription: SubscriptionSummary | null | undefined): string | null {
  if (subscription === undefined) return null;
  if (subscription === null) return PLAN_FREE.id;
  return isEntitledStatus(subscription.status) ? subscription.planId : PLAN_FREE.id;
}

/** Plan ids this build knows how to reason about. */
const KNOWN_PLAN_IDS: ReadonlySet<string> = new Set(bundledPlans().map((plan) => plan.id));

/** Stable identity, so the provider's memo does not churn on every render. */
const EMPTY_OVERRIDES: Record<string, unknown> = {};

/**
 * The resolved plan, or `null` when it is not known.
 *
 * `null` covers three distinct situations that must behave identically — a
 * read that has not landed or failed, an entitled row naming no plan, and a
 * plan id this build does not recognise. The last is the subtle one: a bespoke
 * enterprise tier minted server-side would otherwise fall through
 * `resolvePlanById` to FREE and strip the paid features of the customer paying
 * the most, which is the exact inversion of their contract.
 *
 * NO row is not among them. That read succeeded, and its answer is the free
 * tier — reading it as unknown made skipping subscription creation the way to
 * every packaged feature, and the authorizer no longer does.
 *
 * Deliberately NOT `resolvePlanById` alone. That one is right for the Billing
 * page — there an id it cannot resolve is DISPLAYED as the default tier rather
 * than enforced — and wrong for anything that hides a control.
 */
export function planFor(subscription: SubscriptionSummary | null | undefined): Plan | null {
  const id = planIdFor(subscription);
  if (!id || !KNOWN_PLAN_IDS.has(id)) return null;
  return resolvePlanById(id);
}

function useCurrentSubscriptionRow(): SubscriptionSummary | null | undefined {
  const client = useApiClient();
  const source = React.useMemo(() => createApiSubscriptionsSource(client), [client]);

  const query = useQuery({
    queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
    queryFn: () => source.getCurrentSubscription(),
    // A plan does not change mid-session, and this read gates nothing the
    // backend is not already gating — so it is worth exactly one call. The one
    // exception is a plan REFUSAL, which proves this copy stale and invalidates
    // the key (`planRefusals.ts`).
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  return query.data;
}

export function useCurrentPlanId(): string | null {
  return planIdFor(useCurrentSubscriptionRow());
}

/**
 * The plan and the subscription's entitlement overrides, for `<PlanProvider>`.
 *
 * The overrides go over RAW: `grantedEntitlements` layers them, and it is the
 * mirror of the Python the authorizer runs. Resolving them here too would be a
 * second implementation of the one piece of arithmetic that decides what a
 * customer bought, and the two would disagree about a withdrawn feature long
 * before anyone noticed.
 */
export function useCurrentPlan(): {
  plan: Plan | null;
  overrides: Record<string, unknown>;
  metadata: Record<string, unknown>;
} {
  const subscription = useCurrentSubscriptionRow();
  return {
    plan: planFor(subscription),
    overrides: subscription?.entitlementOverrides ?? EMPTY_OVERRIDES,
    // The row's own bag, which can carry a per-customer `load_profile`. A
    // bespoke contract ("this customer runs enormous investigations and we
    // agreed to carry it") is expressed here rather than by minting a plan for
    // one customer.
    metadata: subscription?.metadata ?? EMPTY_OVERRIDES,
  };
}
